/**
 * Community Audit Hero Image Component
 *
 * Displays the community logo or placeholder in the audit context.
 * Handles loading states and fallback display for missing images.
 */

import type { Community } from '@communities/community/domain';
import type { GraphQLCommunity } from '@internal-audit/domain/graphql-types';
import { Shimmer } from '@soteria/design-system-ve';

import * as styles from './CommunityAuditHeroImage.css';

interface CommunityAuditHeroImageProps {
  community: Community | GraphQLCommunity | null;
  isLoading?: boolean;
}

export function CommunityAuditHeroImage({ community, isLoading = false }: CommunityAuditHeroImageProps) {
  const name = community?.name || '';
  const logoUrl = community?.logoUrl;
  const heroImageAlt = `${name} community image`;

  // Render loading state
  if (isLoading || !community) {
    return (
      <div className={styles.heroImageContainer}>
        <Shimmer style={{ width: '100%', height: '100%', borderRadius: '8px' }} />
      </div>
    );
  }

  return (
    <div className={styles.heroImageContainer}>
      {logoUrl ? (
        <img alt={heroImageAlt || `${name} community image`} className={styles.heroImage} src={logoUrl} />
      ) : (
        <div className={styles.heroImagePlaceholder}>
          <div>
            {name}
            <br />
            Community
          </div>
        </div>
      )}
    </div>
  );
}
